import { useCallback } from "react";
import useSWRImmutable from "swr/immutable";
import useSWRMutation from "swr/mutation";
import { Paginated, PaginationQuery, UpdateOrderRequestBody, UpdateVisibilityRequestBody } from "../../types";
import { buildUrl, mutateCache, useApi } from "../api";
import { CustomError } from "../errors";
import { Base, InsertBaseBody, UpdateBaseBody } from "./types/bases";

export const useBasesApi = () => {
  const api = useApi();

  const getAdminBase = useCallback(
    (baseId: string, signal: AbortSignal) => {
      const url = `/admin/settings/border/bases/${baseId}`;
      return useSWRMutation<Base, CustomError, string>(url, (url) => api.get<Base>({ url, signal }));
    },
    [api],
  );

  const useAdminBases = useCallback(
    (query: PaginationQuery, signal: AbortSignal) => {
      const url = buildUrl("/admin/settings/border/bases", query);
      return useSWRImmutable<Paginated<Base>, CustomError, string>(url, (url) =>
        api.get<Paginated<Base>>({ url, signal }),
      );
    },
    [api],
  );

  const getAdminBases = useCallback(
    (query: PaginationQuery, signal: AbortSignal) => {
      const url = buildUrl(`/admin/settings/border/bases`, query);
      return api.get<Paginated<Base>>({ url, signal }).then((x) => x.items);
    },
    [api],
  );

  const insertAdminBase = useCallback(
    async (body: InsertBaseBody): Promise<{ id: string }> => {
      const url = `/admin/settings/border/bases`;
      const id = await api.post<{ id: string }, InsertBaseBody>(url, body);
      mutateCache([url]);
      return id;
    },
    [api],
  );

  const updateAdminBase = useCallback(
    async (baseId: string, body: UpdateBaseBody): Promise<void> => {
      const url = `/admin/settings/border/bases/${baseId}`;
      await api.put<UpdateBaseBody>(url, body);
      mutateCache(["/admin/settings/border/bases", "/admin/settings/border/waterproof_kits"]);
    },
    [api],
  );

  const updateAdminBaseVisibility = useCallback(
    async (baseId: string, visibility: UpdateVisibilityRequestBody): Promise<void> => {
      const url = `/admin/settings/border/bases/${baseId}/visibility`;
      (await api.put<UpdateVisibilityRequestBody>(url, visibility),
        mutateCache(["/admin/settings/border/bases", "/admin/settings/border/waterproof_kits"]));
    },
    [api],
  );

  const updateAdminBaseOrder = useCallback(
    async (baseId: string, order: UpdateOrderRequestBody): Promise<void> => {
      const url = `/admin/settings/border/bases/${baseId}/order`;
      (await api.put<UpdateOrderRequestBody>(url, order),
        mutateCache(["/admin/settings/border/bases", "/admin/settings/border/waterproof_kits"]));
    },
    [api],
  );

  const deleteAdminBase = useCallback(
    async (baseId: string): Promise<void> => {
      const url = `/admin/settings/border/bases/${baseId}`;
      await api.del(url);
      mutateCache([`/admin/settings/border/bases`]);
    },
    [api],
  );

  return {
    getAdminBase,
    useAdminBases,
    insertAdminBase,
    updateAdminBase,
    updateAdminBaseVisibility,
    updateAdminBaseOrder,
    deleteAdminBase,
    getAdminBases,
  };
};
